// src/pages/ListingsPage.tsx

import { useEffect, useState } from 'react';
import { Search, MapPin, Fuel, Settings, SlidersHorizontal, X, ChevronDown, ChevronLeft, ChevronRight, Gauge } from 'lucide-react';
import { supabase, Listing } from '../lib/supabase';
import { trackPageView } from '../utils/analytics';
import { Link } from '../components/Router';
import { LayoutWithBanners } from '../components/LayoutWithBanners';

const BRANDS = ['Renault', 'Peugeot', 'Volkswagen', 'Mercedes', 'BMW', 'Audi', 'Toyota', 'Hyundai', 'Kia', 'Seat', 'Fiat', 'Ford'];
const FUEL_TYPES = ['Essence', 'Diesel', 'Hybrid', 'Electric'];
const LOCATIONS = ['Tunis', 'Ariana', 'Ben Arous', 'Manouba', 'Sousse', 'Sfax', 'Bizerte', 'Nabeul', 'Monastir', 'Kairouan'];
const ITEMS_PER_PAGE = 9;

// Reusable styles
const STYLES = {
  input: "w-full bg-gray-100 text-gray-900 text-sm rounded-2xl px-4 py-3 border-none outline-none focus:bg-white focus:ring-2 focus:ring-[#D5462E]/10 transition-all placeholder:text-gray-400",
  select: "w-full bg-gray-100 text-gray-900 text-sm rounded-2xl px-4 py-3 border-none outline-none focus:bg-white focus:ring-2 focus:ring-[#D5462E]/10 transition-all appearance-none cursor-pointer",
  card: "bg-white border border-gray-200 rounded-2xl overflow-hidden hover:border-gray-300 transition-all duration-300 hover:shadow-md",
  primaryButton: "bg-[#D5462E] text-white rounded-full px-6 py-3 font-medium hover:bg-[#b83a24] transition-colors text-sm w-full flex justify-center items-center",
  label: "block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 ml-1"
};

// --- Listing Card Component with Carousel ---
const ListingCard = ({ listing }: { listing: Listing }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const images = listing.images && listing.images.length > 0 ? listing.images : [];
  const hasMultipleImages = images.length > 1;

  const nextImage = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  const goToImage = (e: React.MouseEvent, index: number) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentImageIndex(index);
  };

  return (
    <div className={`${STYLES.card} group flex flex-col h-full`}>
      {/* Image Area */}
      <div className="relative aspect-[4/3] bg-gray-100 overflow-hidden">
        <Link to={`/annonces/${listing.slug || listing.id}`} className="block w-full h-full">
          {images.length > 0 ? (
            <img
              src={images[currentImageIndex]}
              alt={`${listing.brand} ${listing.model}`}
              className="w-full h-full object-cover transition-transform duration-500"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-gray-400">
              <Search className="h-10 w-10" />
            </div>
          )}
        </Link>

        {/* Badges */}
        <div className="absolute top-2 left-2 flex gap-1 z-10">
          <span className="bg-white/90 backdrop-blur-sm px-2 py-0.5 rounded text-[10px] font-semibold text-gray-800 border border-gray-100 shadow-sm">
            {listing.year}
          </span>
          {listing.is_new && (
            <span className="bg-green-500 text-white px-2 py-0.5 rounded text-[10px] font-semibold shadow-sm">
              Neuf
            </span>
          )}
        </div>

        {/* Carousel Controls */}
        {hasMultipleImages && (
          <>
            <button
              onClick={prevImage}
              className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/30 hover:bg-black/60 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-20 backdrop-blur-sm"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              onClick={nextImage}
              className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/30 hover:bg-black/60 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-20 backdrop-blur-sm"
            >
              <ChevronRight className="w-4 h-4" />
            </button>

            {/* Dots */}
            <div className="absolute bottom-2 left-0 right-0 flex justify-center gap-1.5 z-20 px-4">
              {images.slice(0, 5).map((_, idx) => (
                <button
                  key={idx}
                  onClick={(e) => goToImage(e, idx)}
                  className={`w-1.5 h-1.5 rounded-full transition-all duration-300 ${
                    idx === currentImageIndex 
                      ? 'bg-white w-3' 
                      : 'bg-white/50 hover:bg-white/80'
                  }`}
                />
              ))}
            </div>
          </>
        )}
      </div>

      {/* Content Area */}
      <Link to={`/annonces/${listing.slug || listing.id}`} className="flex flex-col flex-grow p-3">
        <div className="mb-1">
          <h3 className="text-sm font-semibold text-gray-900 truncate" title={`${listing.brand} ${listing.model}`}>
            {listing.brand} {listing.model}
          </h3>
          <p className="text-[#D4452C] font-bold text-sm">
            {listing.price.toLocaleString()} TND
          </p>
        </div>

        <div className="mt-auto space-y-1.5 pt-2 border-t border-gray-50">
          <div className="flex items-center justify-between text-[11px] text-gray-500">
             <div className="flex items-center gap-1">
              <Gauge className="w-3 h-3 text-gray-400" />
              <span className="truncate max-w-[60px]">{listing.mileage.toLocaleString()}</span>
            </div>
            <div className="flex items-center gap-1">
              <Fuel className="w-3 h-3 text-gray-400" />
              <span className="truncate max-w-[60px]">{listing.fuel_type}</span>
            </div>
          </div>
          <div className="flex items-center justify-between text-[11px] text-gray-500">
             <div className="flex items-center gap-1">
              <Settings className="w-3 h-3 text-gray-400" />
              <span className="truncate max-w-[60px]">{listing.transmission}</span>
            </div>
            <div className="flex items-center gap-1">
              <MapPin className="w-3 h-3 text-gray-400" />
              <span className="truncate max-w-[60px]">{listing.location}</span>
            </div>
          </div>
        </div>
      </Link>
    </div>
  );
};

export function ListingsPage() {
  const [listings, setListings] = useState<Listing[]>([]);
  const [loading, setLoading] = useState(true);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);

  const [filters, setFilters] = useState({
    search: '',
    brand: '',
    minYear: '',
    maxYear: '',
    minPrice: '',
    maxPrice: '',
    fuelType: '',
    location: '',
    sortBy: 'newest'
  });

  useEffect(() => {
    trackPageView('/listings');
  }, []);

  useEffect(() => {
    loadListings();
  }, [filters.sortBy]);

  // Calculate pagination logic
  const totalPages = Math.ceil(listings.length / ITEMS_PER_PAGE);
  const paginatedListings = listings.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const loadListings = async () => {
    setLoading(true);
    try {
      let query = supabase
        .from('listings')
        .select('*')
        .eq('status', 'approved');

      if (filters.sortBy === 'newest') {
        query = query.order('approved_at', { ascending: false });
      } else if (filters.sortBy === 'price_low') {
        query = query.order('price', { ascending: true });
      } else if (filters.sortBy === 'price_high') {
        query = query.order('price', { ascending: false });
      }

      const { data, error } = await query;

      if (error) throw error;

      let filteredData = data || [];

      // Client-side filtering
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        filteredData = filteredData.filter(listing =>
          listing.brand.toLowerCase().includes(searchLower) ||
          listing.model.toLowerCase().includes(searchLower)
        );
      }
      if (filters.brand) filteredData = filteredData.filter(listing => listing.brand === filters.brand);
      if (filters.minYear) filteredData = filteredData.filter(listing => listing.year >= parseInt(filters.minYear));
      if (filters.maxYear) filteredData = filteredData.filter(listing => listing.year <= parseInt(filters.maxYear));
      if (filters.minPrice) filteredData = filteredData.filter(listing => listing.price >= parseFloat(filters.minPrice));
      if (filters.maxPrice) filteredData = filteredData.filter(listing => listing.price <= parseFloat(filters.maxPrice));
      if (filters.fuelType) filteredData = filteredData.filter(listing => listing.fuel_type === filters.fuelType);
      if (filters.location) filteredData = filteredData.filter(listing => listing.location === filters.location);

      setListings(filteredData);
      // Reset to first page when data/filters update
      setCurrentPage(1);
    } catch (error) {
      console.error('Error loading listings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1); // Reset to page 1 on filter change
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    loadListings();
  };

  const clearFilters = () => {
    setFilters({
      search: '', brand: '', minYear: '', maxYear: '', minPrice: '', maxPrice: '', fuelType: '', location: '', sortBy: 'newest'
    });
    loadListings();
  };

  const applyFilters = () => {
    loadListings();
    setShowMobileFilters(false);
  };

  return (
    <div className="min-h-screen bg-gray-50 selection:bg-[#D5462E]/20">
      <LayoutWithBanners 
        pageLocation="listings"
        showTopBanner={true}
        showBottomBanner={true}
        showLeftSidebar={false}
        showRightSidebar={false}
      >
        {/* Header Section - Glassmorphism */}
        <div className="sticky top-0 z-30 px-4 sm:px-6 lg:px-8 py-4 pointer-events-none">
          <div className="bg-white/80 backdrop-blur-xl border border-white/20 rounded-3xl pointer-events-auto shadow-sm">
            <div className="w-full px-6 py-4">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                
                {/* Title */}
                <div className="flex items-center gap-4">
                  <div>
                    <h1 className="text-xl font-semibold text-gray-900 tracking-tight">Toutes les annonces</h1>
                    <div className="flex items-center gap-2 mt-1">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#D4452C]"></div>
                      <p className="text-sm text-gray-500 font-medium">
                        {loading ? 'Chargement...' : `${listings.length} véhicule${listings.length !== 1 ? 's' : ''}`}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Search Bar & Mobile Filter Trigger */}
                <div className="flex items-center gap-3 w-full md:w-auto">
                   <form onSubmit={handleSearch} className="relative flex-1 md:w-80 group">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 group-focus-within:text-[#D4452C] transition-colors" />
                    <input
                      type="text"
                      placeholder="Rechercher (ex: Golf 7...)"
                      value={filters.search}
                      onChange={(e) => handleFilterChange('search', e.target.value)}
                      className={`${STYLES.input} pl-10 rounded-full`}
                    />
                  </form>
                  
                  <button
                    onClick={() => setShowMobileFilters(!showMobileFilters)}
                    className="lg:hidden flex items-center justify-center bg-gray-100 text-gray-900 p-3 rounded-full hover:bg-gray-200 transition-colors flex-shrink-0"
                  >
                    <SlidersHorizontal className="h-5 w-5" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Filters Modal */}
        {showMobileFilters && (
          <div className="lg:hidden fixed inset-0 z-50 flex justify-end">
            <div className="absolute inset-0 bg-gray-900/20 backdrop-blur-sm" onClick={() => setShowMobileFilters(false)} />
            <div className="relative w-full max-w-xs bg-white h-full shadow-none border-l border-gray-200 overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-8">
                  <h2 className="text-xl font-semibold text-gray-900">Filtres</h2>
                  <button
                    onClick={() => setShowMobileFilters(false)}
                    className="bg-gray-100 p-2 rounded-full hover:bg-gray-200 text-gray-500"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
                
                <FilterSection
                  filters={filters}
                  handleFilterChange={handleFilterChange}
                  clearFilters={clearFilters}
                  applyFilters={applyFilters}
                />
              </div>
            </div>
          </div>
        )}

        {/* Main Layout Grid */}
       <div className="w-full px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col lg:flex-row gap-8">
            
            {/* Desktop Sidebar */}
            <div className="hidden lg:block w-72 flex-shrink-0">
              <div className={`sticky top-28 bg-white border border-gray-200 rounded-3xl p-6`}>
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-lg font-semibold text-gray-900">Filtres</h2>
                  <button
                    onClick={clearFilters}
                    className="text-xs font-medium text-[#D4452C] hover:text-[#b83a24] px-3 py-1 bg-[#D4452C]/5 rounded-full hover:bg-[#D4452C]/10 transition-colors"
                  >
                    Réinitialiser
                  </button>
                </div>
                
                <FilterSection
                  filters={filters}
                  handleFilterChange={handleFilterChange}
                  clearFilters={clearFilters}
                  applyFilters={applyFilters}
                />
              </div>
            </div>

            {/* Listings Grid */}
            <div className="flex-1">
              {/* Sorting Bar */}
              <div className="flex justify-end mb-6">
                <div className="relative inline-block">
                  <select
                    value={filters.sortBy}
                    onChange={(e) => handleFilterChange('sortBy', e.target.value)}
                    className="appearance-none bg-white border border-gray-200 pl-4 pr-10 py-2 rounded-full text-sm font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#D5462E]/10 cursor-pointer hover:border-gray-300 transition-colors"
                  >
                    <option value="newest">Plus récentes</option>
                    <option value="price_low">Prix croissant</option>
                    <option value="price_high">Prix décroissant</option>
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                </div>
              </div>

              {loading ? (
                /* Skeleton Loader */
                <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6">
                  {[1, 2, 3, 4, 5, 6].map((i) => (
                    <div key={i} className="bg-white border border-gray-200 rounded-2xl overflow-hidden animate-pulse">
                      <div className="aspect-[4/3] bg-gray-100"></div>
                      <div className="p-3 space-y-3">
                        <div className="h-4 bg-gray-100 rounded-full w-3/4"></div>
                        <div className="h-5 bg-gray-100 rounded-full w-1/2"></div>
                        <div className="h-3 bg-gray-100 rounded-full w-full mt-2"></div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : listings.length > 0 ? (
                <>
                  {/* Listings Grid with Pagination Slicing */}
                  <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6">
                    {paginatedListings.map((listing) => (
                      <ListingCard key={listing.id} listing={listing} />
                    ))}
                  </div>

                  {/* Pagination Controls - Copied from ActualitePage */}
                  {totalPages > 1 && (
                    <div className="flex justify-center mt-8">
                      <nav className="flex items-center space-x-2 bg-white/80 backdrop-blur-xl rounded-full p-2 border border-gray-200">
                        <button 
                          onClick={() => handlePageChange(currentPage - 1)} 
                          disabled={currentPage === 1} 
                          className="p-2 rounded-full hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                        >
                          <ChevronLeft className="h-5 w-5" />
                        </button>
                        
                        <div className="flex space-x-1">
                          {/* We show max 5 pages logic or simple mapping if small number. 
                              For simplicity matching ActualitePage, we map all. 
                              If pages > 10 you might want to slice this array too. */}
                          {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                            <button 
                              key={page} 
                              onClick={() => handlePageChange(page)} 
                              className={`px-4 py-2 rounded-full font-medium transition-all text-sm ${
                                currentPage === page 
                                  ? 'bg-[#D5462E] text-white' 
                                  : 'text-gray-700 hover:bg-gray-100'
                              }`}
                            >
                              {page}
                            </button>
                          ))}
                        </div>
                        
                        <button 
                          onClick={() => handlePageChange(currentPage + 1)} 
                          disabled={currentPage === totalPages} 
                          className="p-2 rounded-full hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                        >
                          <ChevronRight className="h-5 w-5" />
                        </button>
                      </nav>
                    </div>
                  )}
                </>
              ) : (
                <div className="flex flex-col items-center justify-center py-20 bg-white rounded-3xl border border-gray-200">
                  <div className="bg-gray-50 p-4 rounded-full mb-4">
                    <Search className="h-8 w-8 text-gray-400" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900">Aucune annonce trouvée</h3>
                  <p className="text-gray-500 text-sm mt-1 mb-6">Essayez de modifier vos filtres de recherche</p>
                  <button
                    onClick={clearFilters}
                    className="text-[#D4452C] font-medium text-sm hover:bg-[#D4452C]/5 px-4 py-2 rounded-full transition-colors"
                  >
                    Réinitialiser tout
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </LayoutWithBanners>
    </div>
  );
}

// Filter Section Component
function FilterSection({ 
  filters, 
  handleFilterChange, 
  clearFilters, 
  applyFilters 
}: {
  filters: any;
  handleFilterChange: (key: string, value: string) => void;
  clearFilters: () => void;
  applyFilters: () => void;
}) {
  return (
    <div className="space-y-6">
      <div className="space-y-5">
        
        {/* Brand */}
        <div>
          <label className={STYLES.label}>Marque</label>
          <div className="relative">
            <select
              value={filters.brand}
              onChange={(e) => handleFilterChange('brand', e.target.value)}
              className={STYLES.select}
            >
              <option value="">Toutes les marques</option>
              {BRANDS.map(brand => (
                <option key={brand} value={brand}>{brand}</option>
              ))}
            </select>
            <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
          </div>
        </div>

        {/* Year Range */}
        <div>
          <label className={STYLES.label}>Année</label>
          <div className="flex gap-3">
            <input
              type="number"
              placeholder="De"
              value={filters.minYear}
              onChange={(e) => handleFilterChange('minYear', e.target.value)}
              className={STYLES.input}
            />
            <input
              type="number"
              placeholder="À"
              value={filters.maxYear}
              onChange={(e) => handleFilterChange('maxYear', e.target.value)}
              className={STYLES.input}
            />
          </div>
        </div>

        {/* Price Range */}
        <div>
          <label className={STYLES.label}>Prix (TND)</label>
          <div className="flex gap-3">
            <input
              type="number"
              placeholder="Min"
              value={filters.minPrice}
              onChange={(e) => handleFilterChange('minPrice', e.target.value)}
              className={STYLES.input}
            />
            <input
              type="number"
              placeholder="Max"
              value={filters.maxPrice}
              onChange={(e) => handleFilterChange('maxPrice', e.target.value)}
              className={STYLES.input}
            />
          </div>
        </div>

        {/* Fuel Type */}
        <div>
          <label className={STYLES.label}>Carburant</label>
          <div className="relative">
            <select
              value={filters.fuelType}
              onChange={(e) => handleFilterChange('fuelType', e.target.value)}
              className={STYLES.select}
            >
              <option value="">Tous</option>
              {FUEL_TYPES.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
            <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
          </div>
        </div>

        {/* Location */}
        <div>
          <label className={STYLES.label}>Localisation</label>
          <div className="relative">
            <select
              value={filters.location}
              onChange={(e) => handleFilterChange('location', e.target.value)}
              className={STYLES.select}
            >
              <option value="">Toutes</option>
              {LOCATIONS.map(location => (
                <option key={location} value={location}>{location}</option>
              ))}
            </select>
             <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
          </div>
        </div>
      </div>

      <div className="pt-2">
        <button
          onClick={applyFilters}
          className={STYLES.primaryButton}
        >
          Appliquer
        </button>
      </div>
    </div>
  );
}